import { useRef, useState } from 'react'
import { FocusTrapDialog } from '../../components/FocusTrapDialog'
import { InlineAlert } from '../../components/InlineAlert'
import { ApiError } from '../../api/problem'
import { createAccount, patchAccount } from '../../api/accounts'
import { AccountLookupField } from './AccountLookupField'
import { ACCOUNT_STATUS_LABEL, ACCOUNT_TYPE_LABEL } from './columns'
import { ACCOUNT_STATUSES, ACCOUNT_TYPES, type AccountStatus, type AccountType, type AccountView } from '../../types/account'

export interface AccountFormValues {
  name: string
  type: AccountType
  parent: { id: string; name: string } | null
  country: string
  region: string
  industry: string
  status: AccountStatus
}

export interface AccountFormModalProps {
  mode: 'create' | 'edit'
  /** Required in edit mode: the account being edited and its known version (If-Match). */
  editing?: { account: AccountView; version: number }
  onClose: () => void
  onSaved: (account: AccountView) => void
}

const REGION_OPTIONS = ['Americas', 'EMEA', 'APAC']

function initialValues(editing?: AccountFormModalProps['editing']): AccountFormValues {
  if (!editing) {
    return { name: '', type: 'OPERATING', parent: null, country: '', region: '', industry: '', status: 'ACTIVE' }
  }
  const a = editing.account
  return {
    name: a.name,
    type: a.type,
    parent: a.parentAccountId ? { id: a.parentAccountId, name: a.parentAccountName ?? a.parentAccountId } : null,
    country: a.country,
    region: a.region,
    industry: a.industry,
    status: a.status,
  }
}

/** The single New-account / Edit-account form — real `POST`/`PATCH`, real navigation on success. */
export function AccountFormModal({ mode, editing, onClose, onSaved }: AccountFormModalProps) {
  const [values, setValues] = useState<AccountFormValues>(() => initialValues(editing))
  const [submitting, setSubmitting] = useState(false)
  const [error, setError] = useState<string | null>(null)
  const nameRef = useRef<HTMLInputElement>(null)

  function update<K extends keyof AccountFormValues>(key: K, value: AccountFormValues[K]) {
    setValues((prev) => ({ ...prev, [key]: value }))
  }

  const isValid = values.name.trim() !== '' && values.country.trim() !== '' && values.region.trim() !== '' && values.industry.trim() !== ''

  async function handleSubmit() {
    if (!isValid || submitting) return
    setSubmitting(true)
    setError(null)
    try {
      if (mode === 'create') {
        const { account } = await createAccount({
          name: values.name.trim(),
          parentAccountId: values.parent?.id ?? null,
          type: values.type,
          country: values.country.trim(),
          region: values.region.trim(),
          industry: values.industry.trim(),
          status: values.status,
        })
        onSaved(account)
      } else if (editing) {
        const { account } = await patchAccount(
          editing.account.id,
          {
            name: values.name.trim(),
            parentAccountId: values.parent?.id ?? null,
            type: values.type,
            country: values.country.trim(),
            region: values.region.trim(),
            industry: values.industry.trim(),
            status: values.status,
          },
          editing.version,
        )
        onSaved(account)
      }
    } catch (err) {
      if (err instanceof ApiError) {
        setError(err.problem.detail ?? err.message)
      } else {
        setError('Something went wrong saving this account. Please try again.')
      }
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <FocusTrapDialog
      titleId="account-form-title"
      title={mode === 'create' ? 'New account' : `Edit ${editing?.account.name ?? 'account'}`}
      onClose={onClose}
      initialFocusRef={nameRef}
      footer={
        <>
          <button type="button" className="iw-btn" onClick={onClose} disabled={submitting}>
            Cancel
          </button>
          <button
            type="button"
            className="iw-btn iw-btn--primary"
            onClick={handleSubmit}
            disabled={!isValid || submitting}
          >
            {submitting ? 'Saving…' : mode === 'create' ? 'Create account' : 'Save changes'}
          </button>
        </>
      }
    >
      <form
        onSubmit={(e) => {
          e.preventDefault()
          void handleSubmit()
        }}
        style={{ display: 'flex', flexDirection: 'column', gap: 'var(--iw-space-4)' }}
      >
        {error ? <InlineAlert severity="error">{error}</InlineAlert> : null}

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="account-name">
            Name
          </label>
          <input
            id="account-name"
            ref={nameRef}
            className="iw-input"
            value={values.name}
            required
            onChange={(e) => update('name', e.target.value)}
          />
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="account-type">
            Type
          </label>
          <select
            id="account-type"
            className="iw-select"
            value={values.type}
            onChange={(e) => update('type', e.target.value as AccountType)}
          >
            {ACCOUNT_TYPES.map((type) => (
              <option key={type} value={type}>
                {ACCOUNT_TYPE_LABEL[type]}
              </option>
            ))}
          </select>
        </div>

        <AccountLookupField
          id="account-parent"
          label="Parent account (optional)"
          value={values.parent}
          onChange={(next) => update('parent', next)}
          excludeId={editing?.account.id}
        />

        <div style={{ display: 'flex', gap: 'var(--iw-space-3)' }}>
          <div className="iw-field" style={{ flex: 1 }}>
            <label className="iw-field-label" htmlFor="account-country">
              Country
            </label>
            <input
              id="account-country"
              className="iw-input"
              value={values.country}
              required
              onChange={(e) => update('country', e.target.value)}
            />
          </div>
          <div className="iw-field" style={{ flex: 1 }}>
            <label className="iw-field-label" htmlFor="account-region">
              Region
            </label>
            <select
              id="account-region"
              className="iw-select"
              value={values.region}
              onChange={(e) => update('region', e.target.value)}
              required
            >
              <option value="" disabled>
                Select…
              </option>
              {REGION_OPTIONS.map((region) => (
                <option key={region} value={region}>
                  {region}
                </option>
              ))}
            </select>
          </div>
        </div>

        <div className="iw-field">
          <label className="iw-field-label" htmlFor="account-industry">
            Industry
          </label>
          <input
            id="account-industry"
            className="iw-input"
            value={values.industry}
            required
            onChange={(e) => update('industry', e.target.value)}
          />
        </div>

        {mode === 'edit' ? (
          <div className="iw-field">
            <label className="iw-field-label" htmlFor="account-status">
              Status
            </label>
            <select
              id="account-status"
              className="iw-select"
              value={values.status}
              onChange={(e) => update('status', e.target.value as AccountStatus)}
            >
              {ACCOUNT_STATUSES.map((status) => (
                <option key={status} value={status}>
                  {ACCOUNT_STATUS_LABEL[status]}
                </option>
              ))}
            </select>
          </div>
        ) : null}
      </form>
    </FocusTrapDialog>
  )
}
